import { useState } from 'react';
import { CheckCircle2, ChevronRight, Scale } from 'lucide-react';
import RiskBadge from './RiskBadge';
import { cn } from '@/lib/utils';

type RiskLevel = 'critical' | 'high' | 'medium' | 'low' | 'info';

interface RiskAcceptanceItem {
  id: string;
  risk: string;
  riskLevel: RiskLevel;
  rationale: string;
  residualRisk?: RiskLevel;
}

interface RiskAcceptanceMatrixProps {
  items: RiskAcceptanceItem[];
}

/** A deduplicated group: one representative item + all related IDs */
interface RiskGroup {
  representative: RiskAcceptanceItem;
  count: number;
  allIds: string[];
}

const riskOrder: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };

/** Deduplicate items by risk description, keeping the highest-severity representative */
function deduplicateByRisk(items: RiskAcceptanceItem[]): RiskGroup[] {
  const map = new Map<string, RiskGroup>();
  for (const item of items) {
    const key = item.risk;
    const existing = map.get(key);
    if (!existing) {
      map.set(key, { representative: item, count: 1, allIds: [item.id] });
    } else {
      existing.count++;
      existing.allIds.push(item.id);
      if ((riskOrder[item.riskLevel] ?? 4) < (riskOrder[existing.representative.riskLevel] ?? 4)) {
        existing.representative = item;
      }
    }
  }
  return Array.from(map.values());
}

const RiskAcceptanceRow = ({ group }: { group: RiskGroup }) => {
  const [expanded, setExpanded] = useState(false);
  const item = group.representative;

  return (
    <div className="border rounded-lg transition-all hover:shadow-sm">
      <button
        type="button"
        className="w-full flex items-center gap-3 p-4 text-left"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
        aria-label={`${expanded ? 'Collapse' : 'Expand'} ${item.risk}`}
      >
        <ChevronRight
          className={cn(
            'w-4 h-4 shrink-0 text-muted-foreground transition-transform duration-200',
            expanded && 'rotate-90'
          )}
        />
        <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
          <RiskBadge level={item.riskLevel} size="sm" />
          <span className="font-medium text-sm truncate">{item.risk}</span>
          {group.count > 1 && (
            <span className="text-xs bg-muted px-2 py-0.5 rounded-full text-muted-foreground shrink-0">
              {group.count} occurrences
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {item.residualRisk ? (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <span className="hidden sm:inline">Residual:</span>
              <RiskBadge level={item.residualRisk} size="sm" />
            </div>
          ) : (
            <span className="text-xs text-muted-foreground">Residual: N/A</span>
          )}
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 pt-0 ml-7 space-y-3 animate-slide-up">
          <p className="text-sm text-muted-foreground">{item.rationale}</p>

          {group.allIds.length > 0 && (
            <div className="text-xs text-muted-foreground flex flex-wrap gap-1 items-center">
              <span>Related:</span>
              {group.allIds.map((id) => (
                <code key={id} className="bg-muted px-1 rounded">
                  {id}
                </code>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const RiskAcceptanceMatrix = ({ items }: RiskAcceptanceMatrixProps) => {
  if (items.length === 0) {
    return (
      <div className="card-elevated p-8 animate-slide-up flex flex-col items-center gap-3 text-center">
        <CheckCircle2 className="w-10 h-10 text-green-500 opacity-80" />
        <p className="font-semibold text-sm">No findings eligible for risk acceptance</p>
        <p className="text-xs text-muted-foreground max-w-sm">
          Risk acceptance applies to medium and low severity findings where the exposure is
          understood and monitored. Either no such findings were identified, or all findings require
          remediation.
        </p>
      </div>
    );
  }

  const deduplicated = deduplicateByRisk(items);

  return (
    <div className="space-y-6">
      <div className="card-elevated p-6 animate-slide-up">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-gradient-to-r from-primary to-primary/70 p-2 rounded-lg">
            <Scale className="w-5 h-5 text-primary-foreground" />
          </div>
          <h3 className="text-lg font-semibold">Risk Acceptance Decisions</h3>
          <span className="ml-auto bg-muted px-3 py-1 rounded-full text-sm">
            {deduplicated.length} type{deduplicated.length > 1 ? 's' : ''}
            {items.length !== deduplicated.length && (
              <span className="text-muted-foreground"> ({items.length} total)</span>
            )}
          </span>
        </div>

        <div className="space-y-2">
          {deduplicated.map((group) => (
            <RiskAcceptanceRow key={group.representative.id} group={group} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default RiskAcceptanceMatrix;
